import { EntityKindHandler, type EntityMenuContext, type AddPickerEntry, type ToolbarAction, type ToolbarActionContext, type EntityInteractionContext, type PointerDownGuardResult, type DoubleClickResult } from '../EntityKindHandler';
import type { CanvasEntity, ConnectorPosition } from '../../entityTypes';
import { ALL_CONNECTOR_SIDES, getConnectorPointForRect } from '../../entityTypes';
import { CanvasBlockNode } from './BlockNodeNode';
import type { ContextMenuItem } from '../../../components/ContextMenu';
import { Edit2, ToggleLeft, ToggleRight, Layers, Trash2, FileText } from 'lucide-react';
import { buildLayerSubmenuItems } from '../../hooks/useLayerSubmenu';
import { buildGroupSubmenu, buildMultiDeleteItem, buildColorGridSubmenu, buildBorderColorSubmenu } from '../../lib/contextMenuHelpers';
import { canvasSelectionStore } from '../../stores/canvasSelectionStore';
import { BORDER_COLOR_DEFAULTS, BORDER_WIDTH_OPTIONS } from '../../lib/edgeStyleConstants';
import i18n from '../../../i18n';

const t = (key: string) => i18n.t(key as any) as string;

const BG_COLOR_OPTIONS = [
  { id: 'white', label: t('canvasEntity.colorWhite'), color: '#ffffff' },
  { id: 'gray', label: t('canvasEntity.colorGray'), color: '#f3f4f6' },
  { id: 'blue', label: t('canvasEntity.colorBlue'), color: '#dbeafe' },
  { id: 'green', label: t('canvasEntity.colorGreen'), color: '#dcfce7' },
  { id: 'yellow', label: t('canvasEntity.colorYellow'), color: '#fef9c3' },
  { id: 'red', label: t('canvasEntity.colorRed'), color: '#fee2e2' },
  { id: 'purple', label: t('canvasEntity.colorPurple'), color: '#ede9fe' },
];

export class BlockNodeHandler extends EntityKindHandler {
  readonly kind = 'blocknode';
  readonly Component = CanvasBlockNode;
  readonly dataAttribute = 'data-canvas-blocknode';
  canConnect = true;
  canResize = true;
  canInlineEdit = true;

  getAddPickerEntry(): AddPickerEntry {
    return { label: 'BlockNode', icon: FileText, iconColor: '#8b5cf6', priority: 35, mode: 'instant' };
  }

  getConnectorPositions(entity: CanvasEntity): ConnectorPosition[] {
    if (!entity.connectable) return [];
    return ALL_CONNECTOR_SIDES.map(side => ({
      side,
      ...getConnectorPointForRect(entity.x, entity.y, entity.width, entity.height, side),
    }));
  }

  getToolbarActions(ctx: ToolbarActionContext): ToolbarAction[] {
    const { store, entity } = ctx;
    const data = entity.data as any;
    return [
      {
        name: 'backgroundColor',
        label: t('canvasEntity.background'),
        type: 'color',
        options: BG_COLOR_OPTIONS,
        getValue: () => data.backgroundColor,
        apply: (v) => store.send({ type: 'blocknode.updateData', entityId: entity.id, dataChanges: { backgroundColor: v as string } }),
      },
      {
        name: 'borderColor',
        label: t('canvasEntity.borderColor'),
        type: 'color',
        options: BORDER_COLOR_DEFAULTS.filter(c => c.color).map(c => ({ id: c.id, label: c.label, color: c.color })),
        getValue: () => data.borderColor ?? '',
        apply: (v) => {
          const color = v as string;
          if (!color) {
            store.send({ type: 'blocknode.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'blocknode.updateData', entityId: entity.id, dataChanges: { borderColor: color } });
          }
        },
      },
      {
        name: 'borderWidth',
        label: t('canvasEntity.borderWidth'),
        type: 'select',
        options: BORDER_WIDTH_OPTIONS,
        getValue: () => data.borderColor ? String(data.borderWidth ?? 1) : 'none',
        apply: (v) => {
          if (v === 'none') {
            store.send({ type: 'blocknode.updateData', entityId: entity.id, dataChanges: { borderColor: undefined, borderWidth: undefined } });
          } else {
            store.send({ type: 'blocknode.updateData', entityId: entity.id, dataChanges: { borderWidth: Number(v) } });
          }
        },
      },
      {
        name: 'connectable',
        label: 'Connectors',
        type: 'toggle',
        getValue: () => entity.connectable,
        apply: (v) => store.send({ type: 'entity.setConnectable', entityId: entity.id, connectable: !!v }),
      },
    ];
  }

  getContextMenuItems(ctx: EntityMenuContext): ContextMenuItem[] {
    const { store, entity, layers } = ctx;
    const blockNodeId = entity.id;
    const data = entity.data as any;
    const items: ContextMenuItem[] = [];

    // --- Edit ---
    items.push({
      id: 'edit-blocknode',
      label: t('canvasEntity.edit'),
      icon: Edit2,
      onClick: () => {
        canvasSelectionStore.send({ type: 'entity.selectOne', entityId: blockNodeId });
        canvasSelectionStore.send({ type: 'editing.start', elementId: blockNodeId, kind: 'blocknode' });
      },
    });

    // --- Background color submenu ---
    items.push(buildColorGridSubmenu({
      id: 'bg-color-submenu',
      label: t('canvasEntity.backgroundColor'),
      separator: true,
      currentColor: data.backgroundColor,
      handlerColors: [...BG_COLOR_OPTIONS],
      onSelectColor: (color) => store.send({ type: 'blocknode.updateData', entityId: blockNodeId, dataChanges: { backgroundColor: color } }),
    }));

    // --- Border color submenu ---
    items.push(buildBorderColorSubmenu({
      id: 'border-color-submenu',
      label: t('canvasEntity.borderColor'),
      currentColor: data.borderColor,
      onSelectColor: (color) => store.send({ type: 'blocknode.updateData', entityId: blockNodeId, dataChanges: { borderColor: color } }),
      onRemoveBorder: () => store.send({ type: 'blocknode.updateData', entityId: blockNodeId, dataChanges: { borderColor: undefined, borderWidth: undefined } }),
    }));

    // --- Toggle connectors ---
    items.push({
      id: 'toggle-connectors',
      label: entity.connectable ? t('canvasEntity.connectorsOff') : t('canvasEntity.connectorsOn'),
      icon: entity.connectable ? ToggleRight : ToggleLeft,
      onClick: () => store.send({ type: 'entity.setConnectable', entityId: blockNodeId, connectable: !entity.connectable }),
    });

    // --- Gruppe submenu ---
    items.push({ ...buildGroupSubmenu(ctx), separator: true });

    // --- Layer submenu ---
    items.push({
      id: 'layer-submenu',
      label: 'Layer',
      icon: Layers,
      iconColor: '#4f46e5',
      children: buildLayerSubmenuItems(layers, entity.layerId, (layerId) => {
        store.send({ type: 'entity.setLayer', entityId: blockNodeId, layerId });
      }),
    });

    // --- Remove ---
    items.push({
      id: 'remove',
      label: t('canvasEntity.remove'),
      icon: Trash2,
      iconColor: '#dc2626',
      separator: true,
      onClick: () => {
        store.send({ type: 'entity.remove', entityId: blockNodeId });
        canvasSelectionStore.send({ type: 'entity.clear' });
      },
    });

    // --- Multi-delete ---
    const multiDelete = buildMultiDeleteItem(ctx, t('canvasEntity.elements'));
    if (multiDelete) {
      items.push(multiDelete);
    }

    return items;
  }

  onPointerDownGuard(ctx: EntityInteractionContext, _e: React.PointerEvent): PointerDownGuardResult | null {
    if (ctx.isEditing) return { handled: true };
    return null;
  }

  onDoubleClick(ctx: EntityInteractionContext, _e: React.MouseEvent): DoubleClickResult | null {
    if (ctx.redirectGroupId) return null; // let default group-enter happen
    if (ctx.isLocked) return { handled: true };
    canvasSelectionStore.send({ type: 'editing.start', elementId: ctx.entity.id, kind: 'blocknode' });
    return { handled: true };
  }

}
